import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { 
  Camera, 
  Upload, 
  X, 
  Check,
  ChevronRight,
  Loader2,
  Wrench,
  AlertCircle,
  FileText,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common repair issues by instrument type
const REPAIR_ISSUES = {
  guitar: [
    'Broken string(s)',
    'Fret buzzing',
    'Tuning peg issues',
    'Electronics not working',
    'Neck adjustment needed',
    'Bridge/saddle issues',
    'Crack or damage',
    'Other'
  ],
  keyboard: [
    'Keys not working',
    'Stuck keys',
    'No sound output',
    'Power issues',
    'Screen problems',
    'MIDI not working',
    'Pedal issues',
    'Other'
  ],
  drums: [
    'Broken drum head',
    'Hardware issues',
    'Pedal problems',
    'Stand damage',
    'Cymbal crack',
    'Tuning issues',
    'Other'
  ],
  amplifier: [
    'No sound',
    'Crackling/noise',
    'Power issues',
    'Speaker blown',
    'Tube replacement',
    'Knob/switch issues',
    'Other'
  ],
  default: [
    'No sound',
    'Physical damage',
    'Electrical issues',
    'Cosmetic repair',
    'Setup/adjustment',
    'Other'
  ]
};

/**
 * RepairQuoteFlow - Multi-step repair quote request with photo upload
 * Features: Issue selection, photo upload, description, contact info
 */
const RepairQuoteFlow = ({ 
  open, 
  onClose, 
  provider,
  onQuoteSubmitted 
}) => {
  const [step, setStep] = useState(1); // 1: Instrument, 2: Issues, 3: Photos, 4: Details, 5: Review
  const [instrumentType, setInstrumentType] = useState('');
  const [instrumentDetails, setInstrumentDetails] = useState({
    brand: '',
    model: '',
    year: ''
  });
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [otherIssue, setOtherIssue] = useState('');
  const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_contact: 'phone'
  });
  const [serviceMode, setServiceMode] = useState('drop_off');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const issues = REPAIR_ISSUES[instrumentType] || REPAIR_ISSUES.default;

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per photo.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          file: file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (id) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };

  const toggleIssue = (issue) => {
    setSelectedIssues(prev => 
      prev.includes(issue) 
        ? prev.filter(i => i !== issue)
        : [...prev, issue]
    );
  };

  const handleSubmitQuote = async () => {
    setLoading(true);
    try {
      // In real implementation, upload photos first then submit quote
      const quoteData = {
        provider_id: provider.id || provider.provider_id,
        quote_type: 'repair',
        instrument: {
          type: instrumentType,
          brand: instrumentDetails.brand,
          model: instrumentDetails.model,
          year: instrumentDetails.year
        },
        issues: selectedIssues.includes('Other') 
          ? [...selectedIssues.filter(i => i !== 'Other'), otherIssue]
          : selectedIssues,
        description: description,
        urgency: urgency,
        photo_count: photos.length,
        service_mode: serviceMode,
        contact: contactInfo,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${API_URL}/api/services/repair-quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        toast.success('Quote request submitted! You\'ll hear back within 24 hours.');
        onQuoteSubmitted?.();
        onClose();
      } else {
        throw new Error('Quote submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">What instrument needs repair?</h3>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'guitar', label: 'Guitar/Bass' },
                { value: 'keyboard', label: 'Keyboard/Piano' },
                { value: 'drums', label: 'Drums' },
                { value: 'amplifier', label: 'Amplifier' },
                { value: 'string', label: 'String Instrument' },
                { value: 'wind', label: 'Wind Instrument' },
                { value: 'dj', label: 'DJ Equipment' },
                { value: 'other', label: 'Other' }
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setInstrumentType(type.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    instrumentType === type.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <Wrench className={`w-6 h-6 mx-auto mb-2 ${
                    instrumentType === type.value ? 'text-orange-600' : 'text-gray-400'
                  }`} />
                  <p className="font-medium text-sm">{type.label}</p>
                </button>
              ))}
            </div>

            {instrumentType && (
              <div className="pt-4 border-t space-y-3">
                <h4 className="font-medium text-gray-700">Instrument Details (Optional)</h4>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    placeholder="Brand"
                    value={instrumentDetails.brand}
                    onChange={(e) => setInstrumentDetails(d => ({ ...d, brand: e.target.value }))}
                  />
                  <Input
                    placeholder="Model"
                    value={instrumentDetails.model}
                    onChange={(e) => setInstrumentDetails(d => ({ ...d, model: e.target.value }))}
                  />
                  <Input
                    placeholder="Year"
                    value={instrumentDetails.year}
                    onChange={(e) => setInstrumentDetails(d => ({ ...d, year: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">What's the problem?</h3>
            <p className="text-sm text-gray-500">Select all that apply</p>
            
            <div className="grid grid-cols-2 gap-2">
              {issues.map(issue => (
                <button
                  key={issue}
                  onClick={() => toggleIssue(issue)}
                  className={`p-3 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${
                    selectedIssues.includes(issue)
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedIssues.includes(issue)
                      ? 'bg-orange-500 border-orange-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedIssues.includes(issue) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  {issue}
                </button>
              ))}
            </div>

            {selectedIssues.includes('Other') && (
              <Input
                placeholder="Describe the issue..."
                value={otherIssue}
                onChange={(e) => setOtherIssue(e.target.value)}
              />
            )}

            <div className="pt-4">
              <label className="text-sm font-medium text-gray-700">Urgency</label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Not Urgent - Can Wait</SelectItem>
                  <SelectItem value="normal">Normal - Within a Week</SelectItem>
                  <SelectItem value="high">Urgent - Need ASAP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Upload Photos</h3>
            <p className="text-sm text-gray-500">Photos help us provide an accurate quote</p>
            
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-orange-400 transition-colors"
            >
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="font-medium text-gray-700">Click to upload photos</p>
              <p className="text-sm text-gray-500">or drag and drop</p>
              <p className="text-xs text-gray-400 mt-2">Max 5 photos, 5MB each</p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />

            {photos.length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {photos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img 
                      src={photo.preview} 
                      alt="Upload" 
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Good photos help technicians give you an accurate quote faster
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Additional Details</h3>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Describe the problem</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Any additional details about the issue, when it started, what you've tried..."
                rows={4}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Service Mode</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  onClick={() => setServiceMode('drop_off')}
                  className={`p-3 rounded-lg border-2 ${
                    serviceMode === 'drop_off'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200'
                  }`}
                >
                  <p className="font-medium">Drop Off</p>
                  <p className="text-xs text-gray-500">I'll bring it to the shop</p>
                </button>
                <button
                  onClick={() => setServiceMode('pickup')}
                  className={`p-3 rounded-lg border-2 ${
                    serviceMode === 'pickup'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200'
                  }`}
                >
                  <p className="font-medium">Pickup</p>
                  <p className="text-xs text-gray-500">Arrange pickup service</p>
                </button>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <h4 className="font-medium text-gray-700">Contact Information</h4>
              <Input
                placeholder="Your name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Phone *"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
                />
                <Input
                  placeholder="Email *"
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Review Your Quote Request</h3>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Instrument</span>
                <span className="font-medium capitalize">
                  {instrumentType} {instrumentDetails.brand && `- ${instrumentDetails.brand}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Issues</span>
                <span className="font-medium text-right max-w-[200px]">
                  {selectedIssues.join(', ')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Photos</span>
                <Badge variant="outline">
                  <ImageIcon className="w-3 h-3 mr-1" />
                  {photos.length} uploaded
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Urgency</span>
                <Badge className={
                  urgency === 'high' ? 'bg-red-100 text-red-700' :
                  urgency === 'normal' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }>
                  {urgency === 'high' ? 'Urgent' : urgency === 'normal' ? 'Normal' : 'Not Urgent'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service</span>
                <span className="font-medium capitalize">{serviceMode.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Contact</span>
                <span className="font-medium">{contactInfo.name}</span>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <FileText className="w-4 h-4 inline mr-2" />
                {provider.provider_name} will review your request and send a quote within 24 hours
              </p>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-600" />
            Request Repair Quote
          </DialogTitle>
          <DialogDescription>
            from {provider.provider_name || provider.display_name}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                s < step ? 'bg-orange-600 text-white' :
                s === step ? 'bg-orange-100 text-orange-700 border-2 border-orange-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-3 h-3" /> : s}
              </div>
              {s < 5 && <div className={`flex-1 h-1 ${s < step ? 'bg-orange-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < 5 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !instrumentType) ||
                (step === 2 && selectedIssues.length === 0) ||
                (step === 4 && (!contactInfo.name || !contactInfo.phone || !contactInfo.email))
              }
              className="bg-orange-600 hover:bg-orange-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmitQuote}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Quote Request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RepairQuoteFlow;
